export interface AssessmentOption {
  letter: "C" | "I" | "D" | "S"
  label: string
}

export interface AssessmentQuestion {
  id: number
  question: string
  options: AssessmentOption[]
}

export const ASSESSMENT_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 1,
    question: "When meeting new dogs, my dog tends to:",
    options: [
      { letter: "C", label: "Observe quietly before engaging" },
      { letter: "I", label: "Try to make friends straight away" },
      { letter: "D", label: "Take charge of the interaction" },
      { letter: "S", label: "Stay neutral until things settle" },
    ],
  },
  {
    id: 2,
    question: "When new people visit the house, my dog usually:",
    options: [
      { letter: "C", label: "Watches from a distance at first" },
      { letter: "I", label: "Greets them enthusiastically" },
      { letter: "D", label: "Positions themselves between people and space" },
      { letter: "S", label: "Waits calmly for things to normalize" },
    ],
  },
  {
    id: 3,
    question: "On walks in unfamiliar places, my dog:",
    options: [
      { letter: "C", label: "Moves carefully and checks things out" },
      { letter: "I", label: "Gets excited by everything around them" },
      { letter: "D", label: "Leads confidently and pulls ahead" },
      { letter: "S", label: "Stays close and steady" },
    ],
  },
  {
    id: 4,
    question: "When learning a new command, my dog:",
    options: [
      { letter: "C", label: "Wants clear instructions and consistency" },
      { letter: "I", label: "Engages best with praise and enthusiasm" },
      { letter: "D", label: "Pushes ahead and tries their own way" },
      { letter: "S", label: "Learns steadily with repetition" },
    ],
  },
  {
    id: 5,
    question: "When rules are enforced, my dog:",
    options: [
      { letter: "C", label: "Is sensitive to tone and precision" },
      { letter: "I", label: "Responds better when it feels positive" },
      { letter: "D", label: "Tests boundaries" },
      { letter: "S", label: "Accepts them once established" },
    ],
  },
  {
    id: 6,
    question: "If our routine changes, my dog:",
    options: [
      { letter: "C", label: "Seems unsure and needs time to adjust" },
      { letter: "I", label: "Treats it like something exciting" },
      { letter: "D", label: "Adapts quickly and pushes forward" },
      { letter: "S", label: "Prefers when things return to normal" },
    ],
  },
  {
    id: 7,
    question: "In a brand‑new environment, my dog:",
    options: [
      { letter: "C", label: "Scans and assesses before acting" },
      { letter: "I", label: "Explores enthusiastically" },
      { letter: "D", label: "Moves straight in confidently" },
      { letter: "S", label: "Stays close and grounded" },
    ],
  },
  {
    id: 8,
    question: "During grooming or handling, my dog:",
    options: [
      { letter: "C", label: "Notices small details and reacts to them" },
      { letter: "I", label: "Is expressive and communicative" },
      { letter: "D", label: "Tries to control the situation" },
      { letter: "S", label: "Tolerates it calmly" },
    ],
  },
  {
    id: 9,
    question: "During play, my dog prefers to:",
    options: [
      { letter: "C", label: "Figure out toys or games carefully" },
      { letter: "I", label: "Engage socially and interactively" },
      { letter: "D", label: "Compete or dominate the game" },
      { letter: "S", label: "Play gently and predictably" },
    ],
  },
  {
    id: 10,
    question: "If corrected, my dog:",
    options: [
      { letter: "C", label: "Takes it seriously and adjusts behaviour" },
      { letter: "I", label: "Responds better if correction feels upbeat" },
      { letter: "D", label: "Pushes back or resists" },
      { letter: "S", label: "Calms down and complies" },
    ],
  },
  {
    id: 11,
    question: "Around food, my dog:",
    options: [
      { letter: "C", label: "Waits carefully for cues" },
      { letter: "I", label: "Gets visibly excited" },
      { letter: "D", label: "Pushes to eat first" },
      { letter: "S", label: "Is patient and routine‑driven" },
    ],
  },
  {
    id: 12,
    question: "With toys or resources, my dog:",
    options: [
      { letter: "C", label: "Is selective and particular" },
      { letter: "I", label: "Wants to share or involve others" },
      { letter: "D", label: "Guards or claims them" },
      { letter: "S", label: "Is relaxed and non‑possessive" },
    ],
  },
  {
    id: 13,
    question: "Energy‑wise, my dog:",
    options: [
      { letter: "C", label: "Uses energy in measured bursts" },
      { letter: "I", label: "Is high‑energy and expressive" },
      { letter: "D", label: "Channels energy into action" },
      { letter: "S", label: "Maintains a steady pace" },
    ],
  },
  {
    id: 14,
    question: "When there's lots of noise or stimulation, my dog:",
    options: [
      { letter: "C", label: "Becomes alert and cautious" },
      { letter: "I", label: "Feeds off the excitement" },
      { letter: "D", label: "Pushes through it confidently" },
      { letter: "S", label: "Stays calm or withdraws slightly" },
    ],
  },
  {
    id: 15,
    question: "When faced with a problem (e.g. obstacle, puzzle), my dog:",
    options: [
      { letter: "C", label: "Works it out carefully" },
      { letter: "I", label: "Looks to others for engagement" },
      { letter: "D", label: "Forces a solution" },
      { letter: "S", label: "Waits patiently" },
    ],
  },
  {
    id: 16,
    question: "When asked to wait, my dog:",
    options: [
      { letter: "C", label: "Holds position carefully" },
      { letter: "I", label: "Struggles if there's distraction" },
      { letter: "D", label: "Breaks early" },
      { letter: "S", label: "Waits calmly" },
    ],
  },
  {
    id: 17,
    question: "In group settings, my dog:",
    options: [
      { letter: "C", label: "Keeps track of what's happening" },
      { letter: "I", label: "Moves between people or dogs" },
      { letter: "D", label: "Naturally asserts presence" },
      { letter: "S", label: "Blends in comfortably" },
    ],
  },
  {
    id: 18,
    question: "After something stressful, my dog:",
    options: [
      { letter: "C", label: "Needs time to reset" },
      { letter: "I", label: "Seeks reassurance or interaction" },
      { letter: "D", label: "Shakes it off quickly" },
      { letter: "S", label: "Returns calmly to baseline" },
    ],
  },
  {
    id: 19,
    question: "When focusing on a task, my dog:",
    options: [
      { letter: "C", label: "Is precise and detail‑oriented" },
      { letter: "I", label: "Is easily distracted" },
      { letter: "D", label: "Locks in with intensity" },
      { letter: "S", label: "Maintains steady focus" },
    ],
  },
  {
    id: 20,
    question: "When unsure what to do, my dog:",
    options: [
      { letter: "C", label: "Looks for clear guidance" },
      { letter: "I", label: "Looks to others for cues" },
      { letter: "D", label: "Makes a decision and acts" },
      { letter: "S", label: "Waits patiently" },
    ],
  },
]
